/**
 * Credit Card Detection Utility
 * Detects card type based on card number patterns
 */

export const cardTypes = {
  visa: {
    name: 'Visa',
    pattern: /^4/,
    lengths: [13, 16, 19],
    cvvLength: 3,
    logo: 'visa'
  },
  mastercard: {
    name: 'Mastercard', 
    pattern: /^5[1-5]|^2[2-7]/,
    lengths: [16],
    cvvLength: 3,
    logo: 'mastercard'
  },
  amex: {
    name: 'American Express',
    pattern: /^3[47]/,
    lengths: [15],
    cvvLength: 4,
    logo: 'amex'
  },
  discover: {
    name: 'Discover',
    pattern: /^6(?:011|5)/,
    lengths: [16, 19],
    cvvLength: 3,
    logo: 'discover'
  }
};

/**
 * Detect card type from card number
 * @param {string} cardNumber - The card number (can include spaces/dashes)
 * @returns {object|null} - Card type object or null if not detected
 */
export const detectCardType = (cardNumber) => {
  if (!cardNumber) return null;
  
  // Remove all non-numeric characters
  const cleanNumber = cardNumber.replace(/\D/g, '');
  
  // Need at least 2 digits for detection
  if (cleanNumber.length < 2) return null;
  
  // Check each card type pattern
  for (const [key, cardType] of Object.entries(cardTypes)) {
    if (cardType.pattern.test(cleanNumber)) {
      return {
        type: key,
        ...cardType
      };
    }
  }
  
  return null;
};

/**
 * Validate card number using Luhn algorithm
 * @param {string} cardNumber - The card number
 * @returns {boolean} - Whether the card number is valid
 */
export const validateCardNumber = (cardNumber) => {
  const cleanNumber = cardNumber.replace(/\D/g, '');
  
  if (cleanNumber.length < 13 || cleanNumber.length > 19) {
    return false;
  }
  
  // Luhn algorithm
  let sum = 0;
  let isEven = false;
  
  for (let i = cleanNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanNumber.charAt(i), 10);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

/**
 * Format card number with appropriate spacing
 * @param {string} cardNumber - The card number
 * @param {string} cardType - The detected card type
 * @returns {string} - Formatted card number
 */
export const formatCardNumber = (cardNumber, cardType = null) => {
  const cleanNumber = cardNumber.replace(/\D/g, '');
  
  if (!cleanNumber) return '';
  
  // American Express uses 4-6-5 format
  if (cardType === 'amex') {
    return cleanNumber
      .substring(0, 15)
      .replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3')
      .trim();
  }
  
  // Diners Club uses 4-6-4 format
  if (cardType === 'diners') {
    return cleanNumber
      .substring(0, 14)
      .replace(/(\d{4})(\d{6})(\d{4})/, '$1 $2 $3')
      .trim();
  }
  
  // Default 4-4-4-4 format for most cards
  return cleanNumber
    .substring(0, 19)
    .replace(/(\d{4})/g, '$1 ')
    .trim();
};

/**
 * Get all supported card types for display
 * @returns {array} - Array of all card type objects
 */
export const getAllCardTypes = () => {
  return Object.entries(cardTypes).map(([key, cardType]) => ({
    type: key,
    ...cardType
  }));
};

/**
 * Check if card number length is valid for detected type
 * @param {string} cardNumber - The card number
 * @param {object} cardType - The detected card type
 * @returns {boolean} - Whether length is valid
 */
export const isValidLength = (cardNumber, cardType) => {
  if (!cardType) return false;
  
  const cleanNumber = cardNumber.replace(/\D/g, '');
  return cardType.lengths.includes(cleanNumber.length);
};
